import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Undo2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Small icon-button that flips a mis-flagged personal-tax entry back into the
 * business ledger (as a pending entry) and removes the personal_entries row.
 *
 * Only rendered when the entry was originally captured from a docket / photo /
 * voice — a purely manual personal entry has no business twin to fall back to.
 */
export default function UndoToBusinessButton({ entry, onDone, testIdSuffix }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  const source = String(entry?.source || "manual").toLowerCase();
  const eligible = ["docket", "photo", "voice"].includes(source);
  if (!eligible) return null;

  const undo = async (e) => {
    e?.stopPropagation?.();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/personal-tax/entries/${entry.id}/to-business`);
      toast.success("Sent back to business queue", {
        action: {
          label: "Open queue",
          onClick: () => nav("/approval"),
        },
        duration: 4500,
      });
      onDone?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not undo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      data-testid={`pt-undo-business-${testIdSuffix || entry.id}`}
      onClick={undo}
      disabled={busy}
      title="Send back to business queue"
      className="w-8 h-8 rounded-lg text-white/30 hover:text-[#FF9500] inline-flex items-center justify-center disabled:opacity-50"
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
    </button>
  );
}
